import { useState } from 'react';
import { SortKey, TableProps } from './table.type';
import {
  renderLoadingRow,
  renderNoDataRow,
  renderTableHeader,
  renderTableRow,
} from './tableHelper';
import { getIdAttributes, sortData } from '../../utils/helper';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';

export const idPrefix: string = 'table';

const Table = <T extends object, KebabAction extends string = string>(
  props: TableProps<T, KebabAction>,
) => {
  const [sortKey, setSortKey] = useState<keyof T | null>(null);
  const [sortOrder, setSortOrder] = useState<SortKey.ASC | SortKey.DESC>(
    SortKey.DESC,
  );
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
  const [checkedSelectedRows, setCheckedSelectedRows] = useState<T[]>([]);
  const [openMenuRowIndex, setOpenMenuRowIndex] = useState<number | null>(null);
  const { isTablet } = useResponsiveLayout();
  const isChecked: boolean = props.selectedRows !== undefined;
  const selectedRows: T[] = isChecked
    ? props.selectedRows!
    : checkedSelectedRows;

  const prefix: string = idPrefix;

  const resolveRowKey = (): keyof T => {
    return (props.rowKey ??
      ('_id' in (props.tableData?.[0] ?? {}) ? '_id' : 'id')) as keyof T;
  };

  const handleRowClick = (row: T, rowIndex: number) => {
    setSelectedRowIndex(null);
    props.onViewDetail?.(row, rowIndex);
  };

  const handleCheckboxChange = (row: T) => {
    const rowKey: keyof T = resolveRowKey();

    const rowId = row[rowKey];
    const alreadySelected = selectedRows.some(
      (row: T) => row[rowKey] === rowId,
    );

    const updatedRows = alreadySelected
      ? selectedRows.filter((row: T) => row[rowKey] !== rowId)
      : [...selectedRows, row];

    if (!isChecked) {
      setCheckedSelectedRows(updatedRows);
    }

    props.onSelectionChange?.(updatedRows);
  };

  const handleKebabClick = (index: number) => {
    setOpenMenuRowIndex((prev) => (prev === index ? null : index));
  };

  const handleAllCheckboxChange = () => {
    const isAllSelected: boolean =
      selectedRows.length === props.tableData.length;
    const updatedRows: T[] = isAllSelected ? [] : props.tableData;

    if (!isChecked) {
      setCheckedSelectedRows(updatedRows);
    }

    props.onSelectionChange?.(updatedRows);
  };

  const allSelected: boolean =
    selectedRows.length === props.tableData?.length &&
    props.tableData.length > 0;

  const handleSort = (key: keyof T) => {
    if (sortKey === key) {
      setSortOrder((prevOrder) =>
        prevOrder === SortKey.ASC ? SortKey.DESC : SortKey.ASC,
      );
    } else {
      setSortKey(key);
      setSortOrder(SortKey.DESC);
    }
  };

  const getSortedData = (): T[] => {
    const tableData: T[] = props.tableData ?? [];
    if (!sortKey) return tableData;
    return sortData(tableData, sortKey, sortOrder);
  };

  return (
    <div {...getIdAttributes(prefix, 'wrapper')}>
      <table
        className="min-w-full z-10 border-spacing-y-2 table-auto"
        {...getIdAttributes(prefix, 'table')}
      >
        <thead
          className="sticky top-0 z-10 bg-white shadow"
          {...getIdAttributes(prefix, 'thead')}
        >
          {renderTableHeader(
            props.tableHeader,
            allSelected,
            handleAllCheckboxChange,
            sortKey,
            sortOrder,
            handleSort,
            isTablet,
          )}
        </thead>
        {props.loading ? (
          renderLoadingRow(props.tableHeader.length + 2)
        ) : getSortedData().length > 0 ? (
          <tbody>
            {getSortedData().map((row: T, rowIndex: number) =>
              renderTableRow(
                row,
                rowIndex,
                props.tableHeader,
                selectedRowIndex,
                handleRowClick,
                selectedRows.some((selectedRow: T) => {
                  const rowKey = resolveRowKey();
                  return selectedRow[rowKey] === row[rowKey];
                }),
                () => handleCheckboxChange(row),
                openMenuRowIndex,
                handleKebabClick,
                (action, row, index) => {
                  props.onKebabSelect(action, row, index);
                  const shouldCloseKebabMenu: boolean =
                    props.onShouldCloseKebabMenu?.(action) ?? true;
                  if (shouldCloseKebabMenu) {
                    setOpenMenuRowIndex(null);
                  }
                },
                props.loadingMenu,
                props.getKebabMenuOptions,
                isTablet,
              ),
            )}
          </tbody>
        ) : (
          renderNoDataRow(props.tableHeader.length + 2)
        )}
      </table>
    </div>
  );
};

export default Table;
